import Button from "../../../components/ui/Button";
import {
  FaArrowRight,
  FaStar,
  FaCircleCheck,
  FaRoute,
  FaWhatsapp,
  FaEnvelope,
} from "react-icons/fa6";
import { motion } from "framer-motion";

const CONSULTATION_URL =
  "https://cal.com/seamlessvisa/consultation";

const TailoredSolutions = () => {

  return (
    <>
      <section className="bg-linear-to-b from-teal-00/10 to-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="relative overflow-hidden bg-white border border-gray-100 rounded-4xl shadow-xs grid grid-cols-1 lg:grid-cols-12 lg:gap-0">
            {/* Left: Content & CTA (Takes 7 columns of 12) */}
            <div className="lg:col-span-7 flex items-center justify-center lg:justify-start p-8 sm:p-12 lg:p-16 order-first">
              <div className="w-full max-w-xl text-center lg:text-left">
                {/* Eyebrow Label */}
                <span className="inline-flex items-center gap-1.5 bg-blue-ea text-blue-00 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-6">
                  ✦ Tailored Solutions
                </span>

                <h2 className="text-3xl font-light font-spartan tracking-tight text-gray-900 sm:text-4xl leading-[1.3] mb-6">
                  Every immigration <br className="hidden sm:block" /> journey
                  is{" "}
                  <span className="text-amber-fe italic mx-1">unique</span>
                  . <br className="hidden sm:block" /> Let's{" "}
                  <span className="text-blue-00 italic mx-1">map yours</span>
                  .
                </h2>

                <p className="text-gray-600 leading-relaxed mb-8">
                  At SeamlessVisa we take the time to understand your situation
                  and map the right visa pathway for you. Whether you're a
                  first-time applicant or navigating a complex case, we're here
                  to help.
                </p>

                {/* Checklist */}
                <ul className="space-y-3.5 mb-8 text-left max-w-lg mx-auto lg:mx-0">
                  {[
                    "1-on-1 consultation with a seasoned immigration expert",
                    "Personalized pathway mapping tailored to your profile",
                    "Clear checklist of required documents and milestones",
                    "Direct communication channels via WhatsApp and Email",
                  ].map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-3 text-sm text-gray-700"
                    >
                      <FaCircleCheck className="text-base text-blue-00 shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                  <Button
                    label="Schedule a Consultation"
                    onClick={() => window.open(CONSULTATION_URL, "_blank")}
                    variant="primary"
                    size="large"
                    endIcon={<FaArrowRight />}
                  />
                </div>

                {/* Styled Contact Quick Links with Icons */}
                <div className="mt-10 pt-8 border-t border-gray-50 text-center lg:text-left">
                  <p className="text-xs font-bold uppercase tracking-widest text-dark-6b mb-4">
                    Have quick questions? Reach out directly
                  </p>
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                    <a
                      href="https://wa.me/2347073254161"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F8F0] text-[#0E7A48] hover:bg-[#D3F2E1] border border-[#CBEED9] text-sm font-semibold transition-all duration-300 shadow-xs"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaWhatsapp className="text-base text-[#128C7E]" />
                      WhatsApp Chat
                    </a>
                    <a
                      href="mailto:hello@seamlessvisa.com"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-ea text-blue-00 hover:bg-blue-100/60 border border-blue-100 text-sm font-semibold transition-all duration-300 shadow-xs"
                    >
                      <FaEnvelope className="text-base text-blue-00" />
                      Email Us
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: The Visual Stack (Takes 5 columns of 12) */}
            <div
              className="lg:col-span-5 relative h-[450px] lg:h-auto min-h-[450px] flex items-center justify-center overflow-hidden bg-white lg:border-l lg:border-gray-100"
              style={{
                backgroundImage:
                  "radial-gradient(#ebe8e8 1.5px, transparent 1.5px)",
                backgroundSize: "20px 20px",
              }}
            >
              {/* Background Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-100/40 rounded-full blur-3xl" />

              {/* Main Card: Case Map Progress */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="relative z-10 bg-white w-[290px] sm:w-[310px] rounded-2xl shadow-xl border border-gray-100 p-6 flex flex-col gap-4"
              >
                <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                  <div className="h-10 w-10 bg-blue-00 rounded-full flex items-center justify-center text-white text-lg font-bold">
                    SV
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Your Visa Pathway Map
                    </p>
                    <p className="text-xs text-gray-500">Custom case roadmap</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-xs shrink-0 mt-0.5 font-bold">
                      ✓
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        1. Eligibility Assessment
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Analyze optimal pathway options
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-00 text-xs shrink-0 mt-0.5 font-bold animate-pulse">
                      •
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">
                        2. Tailored Visa Strategy
                      </p>
                      <p className="text-[11px] text-blue-600 font-medium">
                        Custom step-by-step roadmap
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-3 opacity-60">
                    <div className="w-6 h-6 rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 text-xs shrink-0 mt-0.5 font-bold">
                      3
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-700">
                        3. Review & Submit case
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Precision document checklist
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Floating Chip 1: Success Rate */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{
                  repeat: Infinity,
                  duration: 4,
                  ease: "easeInOut",
                }}
                className="absolute top-12 lg:top-20 right-4 sm:right-8 z-20 bg-gray-900 text-white p-3 rounded-2xl shadow-lg border border-gray-700 flex items-center gap-3 pr-5"
              >
                <div className="h-8 w-8 bg-amber-fe rounded-lg flex items-center justify-center shrink-0">
                  <FaStar className="text-white text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">
                    Success Rate
                  </p>
                  <p className="text-sm font-bold text-amber-200">
                    98% Approved
                  </p>
                </div>
              </motion.div>

              {/* Floating Chip 2: Pathway Route */}
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{
                  repeat: Infinity,
                  duration: 5,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute bottom-12 lg:bottom-24 left-4 sm:left-8 z-20 bg-white p-3 rounded-2xl shadow-lg border border-gray-100 flex items-center gap-3 pr-5"
              >
                <div className="h-8 w-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                  <FaRoute className="text-blue-600 text-sm" />
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">
                    Pathways
                  </p>
                  <p className="text-sm font-bold text-gray-900">Customized</p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <p className="mx-auto mt-8 max-w-3xl text-center text-base leading-relaxed text-dark-6b/70">
            <span className="font-bold text-sm text-dark-6b uppercase">Disclaimer:</span> SeamlessVisa is not a law firm, and nothing on this site should be
            taken as legal advice or as creating an attorney-client
            relationship. Our services are designed to assist with the
            preparation and organisation of immigration applications. For advice
            specific to your situation, please consult a licensed immigration
            attorney.
          </p>
        </div>
      </section>

    </>
  );
};

export default TailoredSolutions;
